<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    }
});

const emits = defineEmits(['update:show', 'accept', 'decline']);

const dataConsent = ref(false);
const ugcConsent = ref(false);
const nameConsent = ref(false);
const showValidation = ref(false);

watch(
    () => props.show,
    (val) => {
        if (val) {
            dataConsent.value = false;
            ugcConsent.value = false;
            nameConsent.value = false;
            showValidation.value = false;
        }
    }
);

const canSubmit = computed(() => dataConsent.value && ugcConsent.value);

const handleClose = () => emits('update:show', false);

const handleDecline = () => {
    handleClose();
    emits('decline');
};

const handleAccept = () => {
    if (!canSubmit.value) {
        showValidation.value = true;

        return;
    }

    handleClose();
    emits('accept', {
        dataConsent: dataConsent.value,
        ugcConsent: ugcConsent.value,
        nameConsent: nameConsent.value
    });
};
</script>

<template>
    <transition name="fade">
        <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" @click.self="handleDecline">
            <div class="relative w-full max-w-2xl rounded-[20px] shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
                <!-- Gold top bar (Figma: gradient #93802F → #E2C750) -->
                <div class="h-[6px] bg-gradient-to-r from-[#93802F] via-[#E2C750] to-[#93802F]"></div>

                <!-- Modal body: dark blue gradient background -->
                <div class="text-white p-8 flex flex-col gap-[30px]" style="background: linear-gradient(to bottom, #072551, #020e20)">
                    <!-- ── HEADER ── -->
                    <div class="flex items-start justify-between gap-4">
                        <div>
                            <!-- Title: Montserrat 24px w600 white -->
                            <h2 class="text-2xl font-bold text-white leading-[31px]">Datenschutz &amp; Teilnahmebedingungen</h2>
                            <!-- Subtitle: SF Pro 18px w400 white -->
                            <p class="font-pro text-lg leading-[23px] text-white/80 mt-[10px]">
                                Bevor Sie an der <strong>Paint the Hopf Challenge</strong> teilnehmen, benötigen wir Ihre Zustimmung zur Datenverarbeitung und zu den
                                Teilnahmebedingungen.
                            </p>
                        </div>
                        <!-- Close button -->
                        <button
                            type="button"
                            class="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/40 hover:bg-white/5 transition-colors"
                            aria-label="Schließen"
                            @click="handleDecline"
                        >
                            <i class="fi fi-rr-cross-small flex items-center text-base"></i>
                        </button>
                    </div>

                    <!-- ── DETAIL LINKS ── -->
                    <div>
                        <p class="font-pro text-lg leading-[23px] text-white/60 mb-[20px]">Details finden Sie hier:</p>
                        <div class="flex flex-col sm:flex-row gap-[20px]">
                            <a
                                href="/teilnahmebedingungen-osterkampagne"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="inline-flex items-center gap-[10px] rounded-full border border-[#006CFF]/70 bg-[#006CFF]/20 px-5 py-[14px] font-bold text-[20px] leading-[24px] text-white hover:bg-[#006CFF]/40 transition-colors"
                            >
                                <i class="fi fi-rr-external-link flex items-center text-sm"></i>
                                Zu den Teilnahmebedingungen
                            </a>
                            <a
                                href="/datenschutz"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="inline-flex items-center gap-[10px] rounded-full border border-[#006CFF]/70 bg-[#006CFF]/20 px-5 py-[14px] font-bold text-[20px] leading-[24px] text-white hover:bg-[#006CFF]/40 transition-colors"
                            >
                                <i class="fi fi-rr-external-link flex items-center text-sm"></i>
                                Zu den Datenschutzhinweisen
                            </a>
                        </div>
                    </div>

                    <!-- ── CONSENT BOXES ── -->
                    <div class="rounded-[20px] bg-white/5 border border-white/10 p-[20px] flex flex-col gap-[20px]">
                        <!-- Checkbox 1: Pflichtfeld (Teilnahmebedingungen + Datenschutz) -->
                        <label class="flex items-start gap-[10px] cursor-pointer">
                            <input
                                v-model="dataConsent"
                                type="checkbox"
                                class="mt-1 w-5 h-5 flex-shrink-0 rounded border-white/30 bg-white/10 text-[#006CFF] accent-[#006CFF] cursor-pointer"
                                @change="showValidation = false"
                            />
                            <span class="font-pro text-lg leading-[23px] text-white">
                                Ich habe die
                                <a href="/teilnahmebedingungen-osterkampagne" target="_blank" class="text-[#E2C750] hover:underline">Teilnahmebedingungen</a>
                                und die
                                <a href="/datenschutz" target="_blank" class="text-[#E2C750] hover:underline">Datenschutzhinweise</a>
                                gelesen und stimme ihnen zu.
                                <span class="text-[#E2C750] font-bold">*</span>
                            </span>
                        </label>

                        <!-- Checkbox 2: Pflichtfeld (UGC-Rechte) -->
                        <label class="flex items-start gap-[10px] cursor-pointer">
                            <input
                                v-model="ugcConsent"
                                type="checkbox"
                                class="mt-1 w-5 h-5 flex-shrink-0 rounded border-white/30 bg-white/10 text-[#006CFF] accent-[#006CFF] cursor-pointer"
                                @change="showValidation = false"
                            />
                            <span class="font-pro text-lg leading-[23px] text-white">
                                Ich stimme zu, dass HKCM mein eingereichtes Design im Rahmen der „Paint the Hopf"-Kampagne veröffentlichen und verwenden darf gemäß den
                                <a href="/nutzungsrechte-ugc" target="_blank" class="text-[#E2C750] hover:underline">Nutzungsrechten für UGC</a>.
                                <span class="text-[#E2C750] font-bold">*</span>
                            </span>
                        </label>

                        <!-- Checkbox 3: Optional (Namensnennung) -->
                        <label class="flex items-start gap-[10px] cursor-pointer">
                            <input
                                v-model="nameConsent"
                                type="checkbox"
                                class="mt-1 w-5 h-5 flex-shrink-0 rounded border-white/30 bg-white/10 text-[#006CFF] accent-[#006CFF] cursor-pointer"
                            />
                            <span class="font-pro text-lg leading-[23px] text-white/80">
                                Optional: Ich bin einverstanden, dass mein Name / Pseudonym im Gewinnfall veröffentlicht werden darf.
                            </span>
                        </label>

                        <!-- Validation Error -->
                        <transition name="slide">
                            <div
                                v-if="showValidation && !canSubmit"
                                class="flex items-center gap-[10px] rounded-[20px] bg-amber-400/15 border border-amber-400/40 px-[20px] py-[14px]"
                            >
                                <i class="fi fi-rr-triangle-warning flex items-center text-amber-400 text-lg"></i>
                                <p class="font-pro text-lg leading-[23px] text-amber-300">
                                    Bitte bestätigen Sie alle Pflichtfelder (<span class="text-[#E2C750]">*</span>), um fortzufahren.
                                </p>
                            </div>
                        </transition>
                    </div>

                    <!-- ── ACTION BUTTONS ── -->
                    <div class="flex flex-col sm:flex-row sm:justify-end gap-[20px]">
                        <!-- Abbrechen -->
                        <button
                            type="button"
                            class="inline-flex justify-center items-center rounded-full border border-white/20 px-5 py-5 font-bold text-[20px] leading-[24px] text-white hover:bg-white/5 hover:border-white/40 transition-colors"
                            @click="handleDecline"
                        >
                            Abbrechen
                        </button>
                        <!-- Einverstanden & teilnehmen -->
                        <button
                            type="button"
                            class="inline-flex justify-center items-center gap-[10px] rounded-full px-5 py-5 font-bold text-[20px] leading-[24px] text-white transition-all"
                            :class="canSubmit ? 'opacity-100 cursor-pointer hover:opacity-90' : 'opacity-40 cursor-not-allowed'"
                            style="background: linear-gradient(to right, #93802f, #e2c750)"
                            @click="handleAccept"
                        >
                            <i class="fi fi-rr-check flex items-center"></i>
                            Einverstanden &amp; teilnehmen
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
    transition: all 0.2s ease-out;
}
.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
